/**
 * Inline SVG icon set — the ONLY sanctioned icons in the app. No emoji in UI.
 *
 * Emoji read as unfinished (🤖 buttons, 🔴 "recording" dots) and render
 * differently per platform. These are minimal 24-viewBox stroke glyphs in the
 * same visual voice as the tab bar icons: stroke=currentColor, round caps,
 * ~1.8 stroke width — so they inherit text color and scale with `className`.
 *
 * Usage: <Icon.Target className="h-4 w-4" />  (size via className; default 1em)
 */
import type { SVGProps } from 'react';

type P = SVGProps<SVGSVGElement>;

function base(props: P) {
  return {
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    width: props.width ?? '1em',
    height: props.height ?? '1em',
    ...props,
  };
}

/** 🎯 target / mark as target */
export function Target(props: P) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="8.2" />
      <circle cx="12" cy="12" r="4.6" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** 🚫 do-not-draft / avoid */
export function Slash(props: P) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M6.5 6.5l11 11" />
    </svg>
  );
}

/** 📝 ✏️ note / edit */
export function Pencil(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M4 20l1-4L16.5 4.5a2.1 2.1 0 013 3L8 19l-4 1z" />
      <path d="M14.5 6.5l3 3" />
    </svg>
  );
}

/** 🤖 mock-draft CPU */
export function Bot(props: P) {
  return (
    <svg {...base(props)}>
      <rect x="5" y="8" width="14" height="11" rx="2.5" />
      <path d="M12 8V4.5M9.5 4.5h5" />
      <circle cx="9.3" cy="13" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="14.7" cy="13" r="0.9" fill="currentColor" stroke="none" />
      <path d="M9.5 16.2h5" />
    </svg>
  );
}

/** 🔗 sync / connect */
export function LinkIcon(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M10 14a4.2 4.2 0 006 0l3-3a4.24 4.24 0 00-6-6l-1.5 1.5" />
      <path d="M14 10a4.2 4.2 0 00-6 0l-3 3a4.24 4.24 0 006 6L12.5 17.5" />
    </svg>
  );
}

/** 📋 report / grade */
export function Clipboard(props: P) {
  return (
    <svg {...base(props)}>
      <rect x="6" y="5" width="12" height="16" rx="2" />
      <path d="M9.5 5a2.5 2.5 0 015 0M9.5 11h5M9.5 15h5" />
    </svg>
  );
}

/** ⇄ swap / replace */
export function Swap(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M4 9h13l-3-3M20 15H7l3 3" />
    </svg>
  );
}

/** ↶ undo */
export function Undo(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M7 5L3.5 8.5 7 12" />
      <path d="M3.5 8.5H14a5.5 5.5 0 010 11H9" />
    </svg>
  );
}

/** 🏃 position run alert */
export function TrendSpike(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M3 17l5-5 3.5 3.5L20 7" />
      <path d="M14.5 7H20v5.5" />
    </svg>
  );
}

/** ✅ / connected state */
export function Check(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M4.5 12.5l5 5L19.5 7" />
    </svg>
  );
}

/** 🟢 live indicator — CSS dot with pulse-friendly fill */
export function LiveDot(props: P) {
  return (
    <svg {...base({ ...props, strokeWidth: 0 })}>
      <circle cx="12" cy="12" r="5.5" fill="currentColor" />
    </svg>
  );
}

/** ⋯ actions overflow */
export function Ellipsis(props: P) {
  return (
    <svg {...base({ ...props, strokeWidth: 0 })}>
      <circle cx="5.5" cy="12" r="1.6" fill="currentColor" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
      <circle cx="18.5" cy="12" r="1.6" fill="currentColor" />
    </svg>
  );
}

/** × remove (heavier than text ×, consistent hit) */
export function Close(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/** › chevron */
export function Chevron(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

/** Draft board grid */
export function Grid(props: P) {
  return (
    <svg {...base(props)}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 10h16M4 15h16M10 4v16M15 4v16" strokeWidth={1.4} />
    </svg>
  );
}

/** Sleeper (integration) — crescent moon monogram, neutral */
export function Moon(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M19 14.5A7.5 7.5 0 019.5 5 7.5 7.5 0 1019 14.5z" />
    </svg>
  );
}

/** ★ queue / favorite — outline by default; add `fill-current` when active */
export function Star(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M12 3.6l2.6 5.3 5.8.85-4.2 4.1 1 5.8-5.2-2.75L6.8 19.65l1-5.8-4.2-4.1 5.8-.85L12 3.6z" />
    </svg>
  );
}

/** ⚠️ warning / conflict */
export function Warning(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M12 4.2L2.9 19.3h18.2L12 4.2z" />
      <path d="M12 10v4.2" />
      <circle cx="12" cy="16.9" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** house / home tab */
export function Home(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M3.5 10.5L12 3.8l8.5 6.7" />
      <path d="M5.5 9.2V20h13V9.2" />
      <path d="M9.8 20v-5.6h4.4V20" />
    </svg>
  );
}

/** person / profile */
export function Person(props: P) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M5.2 19.6c.9-3.4 3.6-5.2 6.8-5.2s5.9 1.8 6.8 5.2" />
    </svg>
  );
}

/** hamburger menu / sidebar toggle */
export function Menu(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

/** gear / settings */
export function Gear(props: P) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="3.1" />
      <path d="M19.4 15a1.7 1.7 0 00.34 1.87l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.7 1.7 0 00-1.87-.34 1.7 1.7 0 00-1 1.55V21a2 2 0 11-4 0v-.09a1.7 1.7 0 00-1-1.55 1.7 1.7 0 00-1.87.34l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.7 1.7 0 00.34-1.87 1.7 1.7 0 00-1.55-1H3a2 2 0 110-4h.09a1.7 1.7 0 001.55-1 1.7 1.7 0 00-.34-1.87l-.06-.06a2 2 0 112.83-2.83l.06.06a1.7 1.7 0 001.87.34 1.7 1.7 0 001-1.55V3a2 2 0 114 0v.09a1.7 1.7 0 001 1.55 1.7 1.7 0 001.87-.34l.06-.06a2 2 0 112.83 2.83l-.06.06a1.7 1.7 0 00-.34 1.87 1.7 1.7 0 001.55 1H21a2 2 0 110 4h-.09a1.7 1.7 0 00-1.55 1z" />
    </svg>
  );
}

/**
 * The Coach's mark — a four-point star burst, drawn filled rather than
 * stroked so it reads as light rather than as another outline glyph. Sits
 * at the centre of the summon sigil in the draft room.
 */
export function Spark(props: P) {
  return (
    <svg {...base({ ...props, strokeWidth: 0 })}>
      <path
        d="M12 2.4c.5 4.3 2.9 6.7 7.2 7.2v.1c-4.3.5-6.7 2.9-7.2 7.2h-.1c-.5-4.3-2.9-6.7-7.2-7.2v-.1c4.3-.5 6.7-2.9 7.2-7.2z"
        fill="currentColor"
        transform="translate(0 2.3)"
      />
    </svg>
  );
}

/**
 * Outer rune ring for the summon sigil — a broken circle, so the gaps
 * make the rotation visible. Pure decoration; never used alone.
 */
export function SigilRing(props: P) {
  return (
    <svg {...base(props)}>
      <circle
        cx="12"
        cy="12"
        r="10"
        strokeDasharray="7 5.2"
        strokeWidth={1.4}
      />
    </svg>
  );
}

/** Inner rune ring — finer, fewer gaps, turns the other way. */
export function SigilRingInner(props: P) {
  return (
    <svg {...base(props)}>
      <circle
        cx="12"
        cy="12"
        r="7.2"
        strokeDasharray="2.2 4.4"
        strokeWidth={1.1}
      />
    </svg>
  );
}

/** Rankings — the expert board, best first */
export function Trophy(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M8 4h8v5a4 4 0 01-8 0V4z" />
      <path d="M8 5.5H5.2v1a3.3 3.3 0 002.9 3.3" />
      <path d="M16 5.5h2.8v1a3.3 3.3 0 01-2.9 3.3" />
      <path d="M12 13v3.4" />
      <path d="M8.6 20h6.8l-.7-3.6H9.3L8.6 20z" />
    </svg>
  );
}

/** Compare players — two panels, side by side */
export function Columns(props: P) {
  return (
    <svg {...base(props)}>
      <rect x="3.4" y="5" width="7" height="14" rx="1.6" />
      <rect x="13.6" y="5" width="7" height="14" rx="1.6" />
    </svg>
  );
}

/** Rookies — a player the league has not seen yet */
export function PersonPlus(props: P) {
  return (
    <svg {...base(props)}>
      <circle cx="9.8" cy="8" r="3.4" />
      <path d="M3.4 19.4c.8-3.1 3.3-4.9 6.4-4.9.9 0 1.8.15 2.6.45" />
      <path d="M17.6 13.6v6M14.6 16.6h6" />
    </svg>
  );
}

/** Exposure — what share of your rosters a player owns */
export function PieChart(props: P) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="8.3" />
      <path d="M12 3.7V12h8.3" />
    </svg>
  );
}

/** Depth charts — every team, stacked by depth */
export function Layers(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M12 3.6l8 4-8 4-8-4 8-4z" />
      <path d="M4 12l8 4 8-4" />
      <path d="M4 16.2l8 4 8-4" />
    </svg>
  );
}

/** Injuries — the league-wide report */
export function MedicalCross(props: P) {
  return (
    <svg {...base(props)}>
      <path d="M9.6 3.6h4.8v5h5v4.8h-5v5H9.6v-5h-5V8.6h5v-5z" />
    </svg>
  );
}

/** Help — how every feature works */
export function QuestionMark(props: P) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M9.7 9.5a2.4 2.4 0 114 1.9c-.9.8-1.7 1.3-1.7 2.4" />
      <circle cx="12" cy="17" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export const Icon = {
  Home,
  Person,
  Menu,
  Gear,
  Spark,
  SigilRing,
  SigilRingInner,
  Trophy,
  Columns,
  PersonPlus,
  PieChart,
  Layers,
  MedicalCross,
  QuestionMark,
  Target,
  Slash,
  Pencil,
  Bot,
  Link: LinkIcon,
  Clipboard,
  Swap,
  Undo,
  TrendSpike,
  Check,
  LiveDot,
  Ellipsis,
  Close,
  Chevron,
  Grid,
  Moon,
  Star,
  Warning,
};
